import { useAppDispatch, useAppSelector } from '../../../store';
import React, { useCallback, useMemo, useState } from 'react';
import { tasksSelector } from '../model/slice/taskSlice';
import { useToggleState } from '../../../shared/hooks';
import { useGetListTaskInfo } from './useGetListTaskInfo';
import { useGetTaskExecutor } from './useGetTaskExecutor';
import { useGetTaskAuthor } from './useGetTaskAuthor';
import {
    appendSearchParams,
    dateFormatterWithDayjs,
    deleteSearchParams,
} from '../../../shared/helpers';
import { updateCustomTask } from '../api';
import { tasksInformationActions } from '../model/slice/tasksInformation';
import { modifyUrlToRedirect } from '../lib/modifyUrlToRedirect';
import { taskBoardActions } from '../../../store/slices/taskBoardSlice/taskBoardSlice';

export const useTaskData = (uid: string, isDone: boolean) => {
    const taskData = useAppSelector((state) => tasksSelector.selectById(state, uid));
    const dispatch = useAppDispatch();
    const [taskIsDone, setTaskIsDone] = useState(isDone);
    const { isModalLoading } = useAppSelector((state) => state.tasksReducer);
    const [isHovered, toggleHover] = useToggleState();

    const {
        tagsFormattedToEditStore,
        taskTags,
        taskColor: boardColor,
        isOverdue,
    } = useGetListTaskInfo(uid);
    const { executorName, executorAvatar } = useGetTaskExecutor(taskData?.involved?.executor_id);
    const { authorName } = useGetTaskAuthor(taskData?.involved?.author_id);

    const dateStartFormatted = useMemo(
        () => dateFormatterWithDayjs(taskData?.date_start || ''),
        [taskData?.date_start]
    );
    const dateEndFormatted = useMemo(
        () => dateFormatterWithDayjs(taskData?.date_end || ''),
        [taskData?.date_end]
    );

    const createdAtFormatted = useMemo(
        () =>
            dateFormatterWithDayjs(
                taskData?.context?.created_at?.split('T')[0],
                'DD.MM.YYYY',
                'YYYY-MM-DD'
            ),
        [taskData?.context?.created_at]
    );

    const handleDoneTaskClick = useCallback(
        async (e: React.SyntheticEvent) => {
            e.stopPropagation();
            if (isModalLoading) return;
            if (taskData?.context?.highest_role === 'observer') return;
            setTaskIsDone((prev) => !prev);

            const {
                title,
                description,
                date_start,
                date_end,
                context,
                involved,
                estimate_id,
                project_id,
                attachments,
            } = taskData;

            const taskDataToSend = {
                taskUid: uid,
                isCompleted: !taskIsDone,
                title,
                description,
                dateStart: date_start ?? '',
                dateEnd: date_end ?? '',
                color: context.color,
                status: context.status,
                entityUid: context.entity_uid,
                estimateId: estimate_id,
                projectId: project_id,
                executorId: involved.executor_id,
                observerIds: involved.observer_ids,
                isLoading: false,
                error: '',
                context: { ...taskData?.context, tags: tagsFormattedToEditStore },
                tags: tagsFormattedToEditStore,
                attachments,
            };

            const res = await dispatch(updateCustomTask(taskDataToSend)).unwrap();
            dispatch(tasksInformationActions.updateTabs(res.newTabs));
        },
        [isModalLoading, taskData, taskIsDone, uid, dispatch, tagsFormattedToEditStore]
    );

    const handleCustomTaskClick = useCallback(
        (e: React.SyntheticEvent) => {
            e.stopPropagation();

            const stringUid = uid.toString();
            appendSearchParams('task_id', stringUid);
            dispatch(taskBoardActions.onModalMode('editTask'));
        },
        [uid, dispatch]
    );

    const handleTaskClick = useCallback(
        (e: React.SyntheticEvent) => {
            e.stopPropagation();
            if (taskData.type !== 'system') {
                deleteSearchParams('task_id');
                handleCustomTaskClick(e);
                return;
            }

            const modifiedUrl = modifyUrlToRedirect(
                document.location.href,
                taskData?.context?.redirect_url
            );
            if (modifiedUrl) {
                window.open(modifiedUrl, '_blank');
            } else {
                console.error('Invalid URL for redirection');
            }
        },
        [taskData, handleCustomTaskClick]
    );

    return {
        taskData,
        taskIsDone,
        setTaskIsDone,
        isModalLoading,
        isHovered,
        toggleHover,
        boardColor,
        isOverdue,
        tagsFormattedToEditStore,
        taskTags,
        executorName,
        executorAvatar,
        authorName,
        dateStartFormatted,
        dateEndFormatted,
        createdAtFormatted,
        handleDoneTaskClick,
        handleTaskClick,
        dispatch,
    };
};
